import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Trophy, Flame, MapPin, Calendar, Award, X, Sparkles, ArrowRight, TrendingUp } from "lucide-react";
import StravaConnectButton from "@/components/StravaConnectButton";
import PoweredByStrava from "@/components/PoweredByStrava";
import EventDetailDialog from "@/components/EventDetailDialog";
import ShareEventPromo from "@/components/ShareEventPromo";
import WelcomeBoard from "@/components/WelcomeBoard";
import RaceDayReminder from "@/components/RaceDayReminder";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [events, setEvents] = useState([]);
  const [topRunners, setTopRunners] = useState([]);
  const [hideBanner, setHideBanner] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [monthly, setMonthly] = useState(null);
  const [featuredEvent, setFeaturedEvent] = useState(null);

  useEffect(() => {
    api.get("/challenges").then((r) => setChallenges(r.data));
    api.get("/events").then((r) => setEvents(r.data.slice(0, 4)));
    api.get("/leaderboard").then((r) => setTopRunners(r.data.slice(0, 5)));
    api.get("/me/monthly").then((r) => setMonthly(r.data)).catch(() => {});
    api.get("/settings").then((r) => setFeaturedEvent(r.data?.featured_event || null)).catch(() => {});
    const dismissed = localStorage.getItem("oscr_strava_banner_dismissed") === "1";
    setHideBanner(dismissed);
    if (localStorage.getItem("oscr_show_welcome") === "1") {
      setShowWelcome(true);
      localStorage.removeItem("oscr_show_welcome");
    }
  }, []);

  const dismissBanner = () => {
    localStorage.setItem("oscr_strava_banner_dismissed", "1");
    setHideBanner(true);
  };

  if (!user) return null;
  const stravaConnected = !!user.strava_connected;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10" data-testid="dashboard-page">
      {/* Welcome banner for first-time visitors after onboarding */}
      {showWelcome && (
        <div
          data-testid="welcome-banner"
          className="relative hairline border-orange-600/50 bg-gradient-to-r from-orange-600/15 via-orange-600/5 to-transparent p-6 mb-8 overflow-hidden"
        >
          <div className="absolute -top-8 -right-8 w-40 h-40 bg-orange-600/10 rounded-full blur-3xl pointer-events-none" />
          <button
            onClick={() => setShowWelcome(false)}
            className="absolute top-3 right-3 text-zinc-500 hover:text-white p-1 transition"
            title="Dismiss"
            data-testid="dismiss-welcome-banner"
          >
            <X size={16} />
          </button>
          <div className="flex items-start gap-4 relative">
            <div className="hairline border-orange-600 bg-orange-600 p-2.5 shrink-0">
              <Sparkles className="text-white" size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase">
                THE PACK WELCOMES YOU
              </p>
              <h2 className="font-display text-2xl sm:text-3xl text-white mt-1 leading-tight">
                Namaskar, {user.name.split(" ")[0]}. <span className="text-orange-500">You&apos;re in.</span>
              </h2>
              <p className="text-zinc-300 text-sm mt-2 max-w-2xl">
                You just joined <span className="text-white font-semibold">{user.city || "Odisha"}</span>&apos;s
                strongest running community. Here&apos;s what to do next:
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/activity">
                  <Button size="sm" data-testid="welcome-cta-activity" className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider text-xs">
                    LOG YOUR FIRST RUN
                  </Button>
                </Link>
                <Link to="/challenges">
                  <Button size="sm" variant="outline" data-testid="welcome-cta-challenges" className="rounded-none border-zinc-700 bg-transparent text-white hover:bg-zinc-900 font-display tracking-wider text-xs">
                    JOIN A CHALLENGE
                  </Button>
                </Link>
                <Link to="/events">
                  <Button size="sm" variant="outline" data-testid="welcome-cta-events" className="rounded-none border-zinc-700 bg-transparent text-white hover:bg-zinc-900 font-display tracking-wider text-xs">
                    SEE UPCOMING RUNS
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Strava connect banner — shown until user connects or dismisses */}
      {!stravaConnected && !hideBanner && (
        <div className="hairline bg-orange-600/10 border-orange-600/40 p-4 mb-8 flex items-center justify-between gap-4 flex-wrap" data-testid="strava-banner">
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase">CONNECT STRAVA</p>
            <p className="text-white mt-1 font-display text-lg tracking-wider">
              AUTO-SYNC YOUR RUNS. NEVER LOG MANUALLY AGAIN.
            </p>
            <p className="text-zinc-400 text-sm mt-1">
              Connect your Strava once — every run, walk and ride you upload to Strava lands on OSCR automatically.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StravaConnectButton connected={false} onChange={refresh} />
            <button onClick={dismissBanner} className="text-zinc-500 hover:text-white p-1" title="Dismiss" data-testid="dismiss-strava-banner">
              <X size={16} />
            </button>
          </div>
        </div>
      )}
      {stravaConnected && (
        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <StravaConnectButton connected={true} username={user.strava_username} onChange={refresh} />
          <PoweredByStrava size="sm" />
        </div>
      )}

      {/* HERO — editorial cover for the dashboard */}
      <section
        data-testid="dashboard-hero"
        className="relative hairline bg-zinc-950 overflow-hidden mt-2 editorial-lift"
      >
        {/* Athletic background imagery — grayscale + slow parallax */}
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1552674605-db6ffd4facb5?auto=format&fit=crop&w=1920&q=70"
            alt=""
            className="w-full h-full object-cover cover-breathe grayscale contrast-[1.05]"
            loading="lazy"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/40" />

        <div className="relative px-6 sm:px-10 py-12 sm:py-16 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 hairline bg-black/60 border-orange-600/40 mb-5">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
              <span className="font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase">
                {greetingByHour()} · {new Date().toLocaleDateString(undefined, { weekday: "long" }).toUpperCase()}
              </span>
            </div>
            <h1 className="font-editorial text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.88] tracking-tight">
              {greetingByHour() === "GOOD MORNING" ? "Morning," : greetingByHour() === "GOOD EVENING" ? "Evening," : "Hey,"}{" "}
              <span className="text-orange-500">{user.name.split(" ")[0]}.</span>
            </h1>
            <p className="text-zinc-300 mt-4 max-w-xl text-sm sm:text-base leading-relaxed">
              {motivationalLine(user.total_km || 0)}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/activity">
                <Button data-testid="hero-log-run-btn" className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider px-5">
                  LOG TODAY&apos;S RUN <ArrowRight size={16} className="ml-1" />
                </Button>
              </Link>
              <Link to={`/profile/${user.user_id}`}>
                <Button variant="outline" data-testid="view-profile-btn" className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/10 font-display tracking-wider">
                  VIEW PROFILE
                </Button>
              </Link>
            </div>
          </div>

          {/* Quick stat strip — Lifetime + This-Month side by side */}
          <div className="shrink-0 grid grid-cols-2 gap-2 min-w-[280px]">
            <div className="hairline bg-black/60 border-orange-600/30 px-5 py-6" data-testid="hero-total-km">
              <p className="font-mono text-[9px] tracking-[0.3em] text-orange-500 uppercase">ALL TIME</p>
              <div className="font-mono text-4xl text-white mt-2 leading-none font-semibold tracking-tight">{user.total_km || 0}</div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase mt-2">KM</p>
            </div>
            <div className="hairline bg-black/60 border-zinc-700 px-5 py-6" data-testid="hero-monthly-km">
              <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-300 uppercase">THIS MONTH</p>
              <div className="font-mono text-4xl text-white mt-2 leading-none font-semibold tracking-tight">{monthly?.total_km ?? "—"}</div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase mt-2">
                {monthly && monthly.month ? `KM · RESETS ${new Date(monthly.month + "-01").toLocaleString(undefined, { month: "short" }).toUpperCase()} 1` : "KM"}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Race-week reminders for races the runner joined (within 7 days) */}
      <RaceDayReminder />

      {/* Welcome board — upcoming races the pack is joining */}
      <WelcomeBoard firstName={user.name.split(" ")[0]} />

      {/* Share the featured event */}
      <ShareEventPromo event={featuredEvent} />

      {/* Stats grid — lifetime + monthly side-by-side */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <StatCard icon={MapPin} label="TOTAL KM · ALL TIME" value={user.total_km || 0} />
        <StatCard icon={Flame} label="MONTH KM · RUN" value={monthly?.run_km ?? 0} />
        <StatCard icon={Flame} label="MONTH KM · WALK" value={monthly?.walk_km ?? 0} />
        <StatCard icon={Calendar} label="DAYS ACTIVE · MONTH" value={`${monthly?.days_active ?? 0} · ${monthly?.best_streak ?? 0} STREAK`} small />
      </div>

      {/* Reset notice */}
      <p className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase mt-3" data-testid="reset-notice">
        Monthly tallies reset on the 1st · lifetime totals never reset
      </p>

      {/* Active challenges */}
      <section className="mt-12">
        <div className="flex items-end justify-between mb-5">
          <h2 className="font-display text-2xl sm:text-3xl text-white">Active challenges</h2>
          <Link to="/challenges" className="font-display text-sm tracking-wider text-orange-500 hover:text-orange-400">
            ALL →
          </Link>
        </div>
        {(() => {
          const liveChallenges = challenges.filter((c) => c.status !== "past");
          return liveChallenges.length === 0 ? (
            <div className="hairline bg-zinc-900 p-8 text-center text-zinc-500 font-mono text-sm">
              No challenges live yet. Check back soon.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {liveChallenges.slice(0, 4).map((c) => (
                <Link key={c.challenge_id} to={`/challenges/${c.challenge_id}`} className="hairline bg-zinc-900 p-6 hover:-translate-y-0.5 transition group">
                  <p className="font-mono text-[10px] tracking-[0.25em] text-orange-500 uppercase">
                    {c.start_date} → {c.end_date}
                  </p>
                  <h3 className="font-display text-xl text-white mt-2 group-hover:text-orange-400 transition">{c.title}</h3>
                  <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{c.description}</p>
                  <div className="mt-4 flex gap-4 text-xs font-mono text-zinc-400">
                    <span>{c.target_days} DAYS</span>
                    {c.target_km_per_day && <span>{c.target_km_per_day} KM/DAY</span>}
                  </div>
                </Link>
              ))}
            </div>
          );
        })()}
      </section>

      {/* Two-column: events + top runners */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-12">
        <section className="lg:col-span-2">
          <div className="flex items-end justify-between mb-5">
            <h2 className="font-display text-2xl sm:text-3xl text-white">Upcoming events</h2>
            <Link to="/events" className="font-display text-sm tracking-wider text-orange-500">ALL →</Link>
          </div>
          {events.length === 0 ? (
            <div className="hairline bg-zinc-900 p-8 text-center text-zinc-500 font-mono text-sm">
              No events scheduled.
            </div>
          ) : (
            <div className="space-y-2">
              {events.map((e) => (
                <EventDetailDialog key={e.event_id} event={e}>
                  <button
                    type="button"
                    data-testid={`dashboard-event-${e.event_id}`}
                    className="w-full text-left hairline bg-zinc-900 p-5 flex justify-between items-center gap-4 hover:border-orange-600/50 hover:-translate-y-0.5 transition-all cursor-pointer group"
                  >
                    <div>
                      <p className="font-mono text-[10px] tracking-[0.25em] text-orange-500 uppercase">
                        {new Date(e.date).toDateString()}
                      </p>
                      <h3 className="font-display text-lg text-white mt-1 group-hover:text-orange-400 transition">{e.title}</h3>
                      <p className="text-xs text-zinc-400 mt-1">{e.location}</p>
                    </div>
                    <span className="rounded-none border border-zinc-700 bg-transparent text-white group-hover:bg-orange-600 group-hover:border-orange-600 font-display tracking-wider text-xs px-3 py-1.5 transition">
                      DETAILS
                    </span>
                  </button>
                </EventDetailDialog>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-display text-2xl sm:text-3xl text-white mb-5">Top runners</h2>
          <div className="hairline bg-zinc-900 divide-y divide-zinc-800">
            {topRunners.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 font-mono text-sm">No data yet</div>
            ) : topRunners.map((r, i) => (
              <div key={r.user_id} className="p-4 flex items-center gap-3">
                <span className="font-display text-2xl text-orange-500 w-6">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{r.name}</p>
                  <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">{r.total_km || 0} km</p>
                </div>
                <Award className="text-zinc-700" size={16} />
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, small }) {
  return (
    <div className="hairline bg-zinc-900/60 backdrop-blur-sm p-6 card-editorial group">
      <Icon className="text-orange-500 group-hover:scale-110 transition-transform duration-500" size={16} />
      <div className={`font-mono ${small ? "text-sm" : "text-3xl"} text-white mt-4 tracking-tight font-semibold`}>{value}</div>
      <div className="font-mono text-[10px] text-zinc-500 tracking-[0.3em] mt-2 uppercase">{label}</div>
    </div>
  );
}

function greetingByHour() {
  const h = new Date().getHours();
  if (h < 5) return "RUNNING LATE";
  if (h < 12) return "GOOD MORNING";
  if (h < 17) return "GOOD AFTERNOON";
  return "GOOD EVENING";
}

function motivationalLine(km) {
  if (km === 0) return "Your first kilometre is the hardest. Lace up — the pack is waiting.";
  if (km < 10) return "Solid start. Keep showing up. Every mile compounds.";
  if (km < 50) return "You're building real fitness now. Stay consistent.";
  if (km < 100) return "Almost at your first century. The Mahanadi Mile badge is in sight.";
  if (km < 250) return "Triple-digit runner. The pack notices. Keep grinding.";
  if (km < 500) return "You're in elite territory. Train smart. Recover harder.";
  if (km < 1000) return "Approaching legend status. Inspire someone today.";
  return "Living legend. Thank you for raising the bar for all of Odisha.";
}
